import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, LockSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, loading: cartLoading } = useCart();
  const [processing, setProcessing] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const handleCheckout = async () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        cart_id: cart.id,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to initiate checkout');
    } finally {
      setProcessing(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">LOADING...</div>
      </div>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 
            className="text-4xl uppercase tracking-tight font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            YOUR CART IS EMPTY
          </h1>
          <p className="text-muted-foreground mb-8">ADD SOME ITEMS TO CONTINUE</p>
          <Link to="/shop/men">
            <Button className="bg-white text-black hover:bg-primary hover:text-white">
              CONTINUE SHOPPING
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const shipping = subtotal >= 150 ? 0 : 12.00;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen" data-testid="checkout-page">
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 py-4 border-b border-border">
        <Link 
          to="/"
          className="text-xs uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
          data-testid="back-to-home"
        >
          <ArrowLeft size={14} />
          BACK TO SHOPPING
        </Link>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="text-4xl sm:text-5xl uppercase tracking-tight font-bold mb-12"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            CHECKOUT
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Order Summary */}
            <div className="order-2 lg:order-1">
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                ORDER SUMMARY
              </h2>
              
              <div className="space-y-4">
                {cart.items.map((item, index) => (
                  <div 
                    key={`${item.product_id}-${item.size}`} 
                    className="flex gap-4 border border-border p-4"
                    data-testid={`checkout-item-${index}`}
                  >
                    <div className="w-20 h-24 bg-muted overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 
                        className="font-bold text-sm uppercase tracking-wide"
                        style={{ fontFamily: "'Oswald', sans-serif" }}
                      >
                        {item.name}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        SIZE: {item.size} / QTY: {item.quantity}
                      </p>
                      <p className="font-bold mt-2">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Section */}
            <div className="order-1 lg:order-2">
              <div className="border border-border p-6 lg:sticky lg:top-24">
                <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  PAYMENT
                </h2>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SUBTOTAL</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">SHIPPING</span>
                    <span>{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
                  </div>
                  {subtotal < 150 && (
                    <p className="text-xs text-muted-foreground">
                      ADD {formatPrice(150 - subtotal)} MORE FOR FREE SHIPPING
                    </p>
                  )}
                  
                  <Separator className="bg-border" />
                  
                  <div className="flex justify-between text-xl">
                    <span className="font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>TOTAL</span>
                    <span className="font-bold" style={{ fontFamily: "'Oswald', sans-serif" }} data-testid="checkout-total">
                      {formatPrice(total)}
                    </span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full mt-8 bg-white text-black hover:bg-primary hover:text-white transition-colors h-14 text-base font-bold uppercase tracking-wide"
                  data-testid="pay-now-btn"
                >
                  <LockSimple size={18} className="mr-2" />
                  {processing ? 'PROCESSING...' : 'PAY NOW'}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  SECURE CHECKOUT POWERED BY STRIPE
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
